import { instanceToInstance, instanceToPlain, plainToInstance } from 'class-transformer'
import { cloneDeep } from 'lodash'

import { RunFlowNodeButton } from '../../../../../NewMessageEditors/NewMessageEditorWithMedia/ButtonsConstructor/Buttons/FlowButtons/RunFlowNodeButton'
import MessageEditorWithMediaData from '../../../../../NewMessageEditors/NewMessageEditorWithMedia/types'
import { CustomNode, NodeTypes } from '../../Nodes'
import { BaseNodeStruct } from '../../Nodes'
import { RunFlowNodeData } from '../../Nodes/Additionally/RunFlow/logic/RunFlowNode'
import { BaseNodeModelData } from '../../Nodes/BaseNode/BaseNodeDataModel'
import { CheckSubscriptionNodeData } from '../../Nodes/Branching/CheckSubscriptionNode/logic/CheckSubscriptionNode'
import { ConditionsNodeData } from '../../Nodes/Branching/ConditionsNode/logic/ConditionsNode'
import { DelayNodeData } from '../../Nodes/Branching/DelayNode/logic/DelayNode'
import ProxyDelayData from '../../Nodes/Branching/DelayNode/logic/Proxy/ProxyDelayData'
import { RandomizerNodeData } from '../../Nodes/Branching/RandomizerNode/logic/RandomizerNode'
import { ActionNodeData } from '../../Nodes/Communications/Action/logic/ActionNode'
import { NotifyGroupAction } from '../../Nodes/Communications/Action/logic/Actions/NotifyGroupAction/NotifyGroupAction'
import { NotifyUsersAction } from '../../Nodes/Communications/Action/logic/Actions/NotifyUsersAction/NotifyUsersAction'
import { EditLastMessageNodeData } from '../../Nodes/Communications/EditLastMessage/logic/EditLastMessageNode'
import { ForwardMessageNodeData } from '../../Nodes/Communications/ForwardMessage/logic/ForwardMessageNode'
import { SendInvoiceNodeData } from '../../Nodes/Communications/SendInvoice/logic/SendInvoiceNode'
import { SendMessageNodeData } from '../../Nodes/Communications/SendMessage/logic/SendMessageNode'
import SendMessageAction from '../../Nodes/Communications/SendMessage/logic/actions/SendMessageAction'
import UserInputAction from '../../Nodes/Communications/SendMessage/logic/actions/UserInputAction'
import { CheckLimitedSubscriptionNodeData } from '../../Nodes/Subscription/CheclLimitedSubscription/logic/CheckLimitedSubscriptionNode'
import { InfoLimitedSubscriptionNodeData } from '../../Nodes/Subscription/InfoLimitedSubscription/logic/InfoLimitedSubscriptionNode'

export class CopyVisitor {
  static visits: { [key: string]: (data: BaseNodeModelData) => BaseNodeModelData | void } = {
    [NodeTypes.SendMessage]: (data) => this.visitSendMessageData(data),
    [NodeTypes.Action]: (data) => this.visitActionData(data),
    [NodeTypes.EditLastMessage]: (data) => this.visitEditLastMessageData(data),
    [NodeTypes.ForwardMessage]: (data) => this.visitForwardMessageData(data),
    [NodeTypes.SendInvoice]: (data) => this.visitSendInvoiceData(data),
    [NodeTypes.CheckSubscription]: (data) => this.visitCheckSubscriptionData(data),
    [NodeTypes.Conditions]: (data) => this.visitConditionsData(data),
    [NodeTypes.Randomizer]: (data) => this.visitRandomizerData(data),
    [NodeTypes.CheckLimitedSubscription]: (data) => this.visitCheckLimitedSubscriptionData(data),
    [NodeTypes.InfoLimitedSubscription]: (data) => this.visitInfoLimitedSubscriptionData(data),
    [NodeTypes.Delay]: (data) => this.visitDelayNodeData(data),
    [NodeTypes.RunFlow]: (data) => this.visitRunFlowData(data),
  }

  static visit(nodes: Array<CustomNode>, toCopy: Array<number>, mouse: {
    x: number,
    y: number
  }): Array<BaseNodeStruct> {
    const copyNodes: Array<BaseNodeStruct> = [
      ...nodes
        .filter(({ id }) => toCopy.includes(id))
        .map((n) => this.getCopy(n))
    ]

    const
      minX = Math.min(...copyNodes.map((node) => node.x)),
      maxX = Math.max(...copyNodes.map((node) => node.x)),
      minY = Math.min(...copyNodes.map((node) => node.y)),
      maxY = Math.max(...copyNodes.map((node) => node.y))

    const width = maxX - minX,
      height = maxY - minY

    const { x, y } = mouse

    copyNodes.forEach((node) => {
      node.x = x - (width / 2) + node.x - minX
      node.y = y - (height / 2) + node.y - minY
    })

    return copyNodes
  }

  static getCopy(node: CustomNode): BaseNodeStruct {
    const model = node.getModel()

    const getDataFn = this.visits[model.data.type]

    return {
      data: getDataFn ? getDataFn(cloneDeep(model.data)) : instanceToInstance(model.data),
      input: CustomNode.LastSocketId += 1,
      ...model.output ? { output: CustomNode.LastSocketId += 1 } : {},
      color: model.color,
      x: model.x,
      y: model.y,
    }
  }

  static replaceBtnOutputs(variant: MessageEditorWithMediaData): void {
    variant.buttons.forEach((row) => {
      row.forEach((b) => {
        if (b instanceof RunFlowNodeButton) {
          b.output = CustomNode.LastSocketId + 1
          CustomNode.LastSocketId += 1
        }
      })
    })
  }

  static visitSendMessageData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof SendMessageNodeData) {
      data.inactive_action.output = CustomNode.LastSocketId + 1
      CustomNode.LastSocketId += 1

      data.actions.forEach((action) => {
        if (action instanceof SendMessageAction) {
          action.variants.forEach((m) => this.replaceBtnOutputs(m))
        }

        if (action instanceof UserInputAction) {
          action.inactive_action.output = CustomNode.LastSocketId + 1
          CustomNode.LastSocketId += 1

          action.actions.filter((nestedAction) => {
            if (nestedAction instanceof NotifyUsersAction || nestedAction instanceof NotifyGroupAction) {
              nestedAction.variants.forEach((m) => {
                this.replaceBtnOutputs(m)
              })
            }
          })
        }
      })

      return instanceToInstance(data)
    }
  }

  static visitActionData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof ActionNodeData) {
      data.actions.forEach((a) => {
        if (a instanceof NotifyUsersAction || a instanceof NotifyGroupAction) {
          a.variants.forEach((m) => this.replaceBtnOutputs(m))
        }
      })

      return instanceToInstance(data)
    }
  }

  static visitEditLastMessageData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof EditLastMessageNodeData) {
      data.variants.forEach((m) => this.replaceBtnOutputs(m))

      return instanceToInstance(data)
    }
  }

  static visitDelayNodeData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof ProxyDelayData) {
      const plain = instanceToPlain(data)

      return plainToInstance(DelayNodeData, plain)
    }
  }

  static visitRunFlowData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof RunFlowNodeData) {
      const plain = instanceToPlain(data)

      return plainToInstance(RunFlowNodeData, plain)
    }
  }

  static visitSendInvoiceData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof SendInvoiceNodeData) {
      data.inactive_action.output = CustomNode.LastSocketId + 1
      CustomNode.LastSocketId += 1

      data.success_output = CustomNode.LastSocketId + 1
      CustomNode.LastSocketId += 1

      return instanceToInstance(data)
    }
  }

  static visitForwardMessageData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof ForwardMessageNodeData) {
      data.fail_output = CustomNode.LastSocketId + 1
      CustomNode.LastSocketId += 1

      return instanceToInstance(data)
    }
  }

  static visitCheckSubscriptionData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof CheckSubscriptionNodeData) {
      data.fail_output = CustomNode.LastSocketId + 1
      CustomNode.LastSocketId += 1

      data.success_output = CustomNode.LastSocketId + 1
      CustomNode.LastSocketId += 1

      return instanceToInstance(data)
    }
  }

  static visitConditionsData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof ConditionsNodeData) {
      data.fail_output = CustomNode.LastSocketId + 1
      CustomNode.LastSocketId += 1

      data.groups.forEach((c) => {
        c.success_output = CustomNode.LastSocketId + 1
        CustomNode.LastSocketId += 1
      })

      return instanceToInstance(data)
    }
  }

  static visitRandomizerData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof RandomizerNodeData) {
      data.buttons.forEach((b) => {
        b.output = CustomNode.LastSocketId + 1
        CustomNode.LastSocketId += 1
      })

      return instanceToInstance(data)
    }
  }

  static visitCheckLimitedSubscriptionData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof CheckLimitedSubscriptionNodeData) {
      data.success_output = CustomNode.LastSocketId + 1
      CustomNode.LastSocketId += 1
      data.fail_output = CustomNode.LastSocketId + 1
      CustomNode.LastSocketId += 1

      return instanceToInstance(data)
    }
  }

  static visitInfoLimitedSubscriptionData = (data: BaseNodeModelData): BaseNodeModelData | void => {
    if (data instanceof InfoLimitedSubscriptionNodeData) {
      data.variants.forEach((m) => this.replaceBtnOutputs(m))

      return instanceToInstance(data)
    }
  }

}
